import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

export default function ResendInviteModal({
  isOpen,
  onClose,
  screeningId,
  jobId,
  candidateToResend, // single candidate object if clicked from row
  selectedCandidatesForShortlist, // array of ids if bulk selected
  candidates, // full array of candidates to look up names
  screeningMeta, // screening object for checking features (hasResumeScoring, hasAssessment, hasInterview)
  fetchCandidates, // function to refresh data
}) {
  const jobTitle = screeningMeta?.jobTitle || '';
  const companyName = screeningMeta?.companyName || 'our company';
  
  const defaultEmail = `We hope you are having a great day!\n\nWe noticed that the following steps are still pending for your application to the ${jobTitle} role at ${companyName}.\n\nPlease use the secure link below to resume and complete your application.\n\nIf you have any questions, please contact us at support@talentcrux.ai.\n\nBest regards,\nTalentCrux Team`;
  const defaultSms = `Hi {{FirstName}}, this is a gentle reminder regarding your application for the ${jobTitle} role at ${companyName}. Our records indicate there are still pending steps. Please check your email for the secure link to complete them. If you have questions, please contact support@talentcrux.ai. Best regards, TalentCrux Team`;

  // Sync default texts whenever screeningMeta changes
  useEffect(() => {
    setCustomEmailMessage(defaultEmail);
    setCustomSmsMessage(defaultSms);
  }, [screeningMeta]);

  const [resendSections, setResendSections] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState({ email: true, whatsapp: false, sms: false });
  const [customEmailMessage, setCustomEmailMessage] = useState('');
  const [customSmsMessage, setCustomSmsMessage] = useState('');
  const [resendingInvite, setResendingInvite] = useState(false);
  const [expandedChannel, setExpandedChannel] = useState('email');

  // When modal closes, reset state
  useEffect(() => {
    if (!isOpen) {
      setResendSections([]);
      setSelectedChannels({ email: true, whatsapp: false, sms: false });
      setCustomEmailMessage(defaultEmail);
      setCustomSmsMessage(defaultSms);
      setExpandedChannel('email');
    }
  }, [isOpen]);

  const screeningSections = [
    screeningMeta?.hasResumeScoring !== false && { key: 'resume', label: 'Resume Upload', icon: '📄', description: 'Ask the candidate to re-upload their resume' },
    screeningMeta?.hasAssessment && { key: 'assessment', label: 'Online Assessment', icon: '📝', description: 'Ask the candidate to retake the assessment' },
    screeningMeta?.hasInterview && { key: 'interview', label: 'AI Interview', icon: '🎙️', description: 'Ask the candidate to redo the AI interview' },
  ].filter(Boolean);

  // Key format is the server's contract: `document:<type>:<subtype>` (lib/screeningDocuments.js).
  // Never part of the default selection — reopening a document deletes the candidate's upload.
  const documentSections = (screeningMeta?.includeDocumentCollection && Array.isArray(screeningMeta?.requestedDocuments)
    ? screeningMeta.requestedDocuments
    : []
  ).map((doc) => ({
    key: `document:${doc.type}:${doc.subtype}`,
    label: doc.label || 'Document',
    icon: '📁',
    description: `Ask the candidate to re-upload ${doc.label || 'this document'}`,
  }));

  const allSections = [...screeningSections, ...documentSections];

  const getFailedSections = (cand) => {
    if (!cand) return [];
    const failed = [];
    if (screeningMeta?.hasResumeScoring !== false && (cand.resumeScore == null || cand.resumeScore === 0)) failed.push('resume');
    if (screeningMeta?.hasAssessment && (cand.assessmentScore == null || cand.assessmentScore === 0)) failed.push('assessment');
    if (screeningMeta?.hasInterview && (cand.interviewScore == null || cand.interviewScore === 0 || cand.interviewDropReason)) failed.push('interview');
    return failed;
  };

  const selectedTargetCandidates = (() => {
    if (candidateToResend) return [candidateToResend];
    const idSet = new Set(selectedCandidatesForShortlist?.map((id) => id?.toString()) || []);
    return (candidates || []).filter((c) => {
      const cid = c.id || c._id;
      return cid != null && idSet.has(cid.toString());
    });
  })();

  const resendTargetName = (() => {
    if (selectedTargetCandidates.length === 0) return 'the selected candidates';
    if (selectedTargetCandidates.length === 1) return selectedTargetCandidates[0].name || selectedTargetCandidates[0].email || 'this candidate';
    return `${selectedTargetCandidates.length} candidates`;
  })();

  const autoFailed = candidateToResend ? getFailedSections(candidateToResend) : [];

  useEffect(() => {
    if (isOpen && resendSections.length === 0) {
      const defaults = autoFailed.length > 0 ? autoFailed : screeningSections.map((s) => s.key);
      setResendSections(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoFailed.length]);

  const toggleSection = (key) => {
    setResendSections((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleChannel = (channelKey, e) => {
    e.stopPropagation();
    setSelectedChannels(prev => ({ ...prev, [channelKey]: !prev[channelKey] }));
    if (!selectedChannels[channelKey]) {
      setExpandedChannel(channelKey);
    }
  };

  const handleSend = async () => {
    const ids = selectedTargetCandidates.map(c => c.id || c._id);
    if (!screeningId || ids.length === 0 || resendSections.length === 0) return;
    
    const isAnyChannelSelected = Object.values(selectedChannels).some(v => v);
    if (!isAnyChannelSelected) {
      toast.error('Please select at least one channel (Email, WhatsApp, or SMS).');
      return;
    }

    setResendingInvite(true);
    
    // Send selected channels as an array for the updated backend
    const channelsArray = Object.keys(selectedChannels).filter(k => selectedChannels[k]);

    try {
      const res = await axios.post(`/api/screenings/${screeningId}/resend-invite`, {
        candidateIds: ids,
        sections: resendSections,
        channels: channelsArray,
        customEmailMessage: customEmailMessage.trim() || undefined,
        customSmsMessage: customSmsMessage.trim() || undefined,
      });

      const { sent, failed, details } = res.data || {};
      fetchCandidates();
      
      if (sent > 0 && (failed || 0) === 0) {
        toast.success(`Invite sent successfully to ${sent} candidate${sent === 1 ? '' : 's'}!`);
        onClose();
      } else if (sent > 0 && failed > 0) {
        toast.success(`Sent to ${sent}; ${failed} failed.`, { duration: 5000 });
        onClose();
      } else if (failed > 0) {
        const reasons = (details?.failed || []).map((f) => f.reason || 'Failed').filter(Boolean);
        toast.error(`Could not send invites${reasons.length ? `: ${reasons[0]}` : ''}.`, { duration: 5000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend invitation');
    } finally {
      setResendingInvite(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
      onClick={() => !resendingInvite && onClose()}
    >
      <div
        className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex-shrink-0 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center">
              <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Resend Application Invite</h3>
              <p className="text-xs text-gray-500 mt-0.5">Configure invite options for <span className="font-medium text-gray-700">{resendTargetName}</span></p>
            </div>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="px-6 py-5 overflow-y-auto custom-scrollbar flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Section 1: Sections to resend */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">1. Select Sections to Resend</h4>
              <div className="space-y-2">
              {allSections.map((section) => {
                const isChecked = resendSections.includes(section.key);
                const wasFailed = autoFailed.includes(section.key);
                return (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => toggleSection(section.key)}
                    className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${isChecked
                        ? 'border-blue-500 bg-blue-50/50'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    <div className={`flex-shrink-0 mt-0.5 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${isChecked ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'}`}>
                      {isChecked && (
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-base leading-none">{section.icon}</span>
                        <span className={`text-sm font-medium ${isChecked ? 'text-blue-700' : 'text-gray-800'}`}>{section.label}</span>
                        {wasFailed && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-600">
                            Incomplete
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">{section.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            {/* Info note */}
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
              <svg className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>Existing data for chosen sections will be cleared and cannot be recovered.</span>
            </div>
            </div>
            {/* Section 2: Channels */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">2. Select Channels &amp; Edit Messages</h4>
              <div className="space-y-3">
              
              {/* EMAIL CHANNEL */}
              <div className={`border-2 rounded-xl overflow-hidden transition-colors ${selectedChannels.email ? 'border-blue-500' : 'border-gray-200'}`}>
                <div 
                  className={`flex items-center justify-between p-3 cursor-pointer ${selectedChannels.email ? 'bg-blue-50/50' : 'bg-white hover:bg-gray-50'}`}
                  onClick={() => setExpandedChannel(expandedChannel === 'email' ? null : 'email')}
                >
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={(e) => toggleChannel('email', e)} className={`flex-shrink-0 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${selectedChannels.email ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'}`}>
                      {selectedChannels.email && <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>}
                    </button>
                    <div className="flex items-center gap-2">
                      <svg className={`h-4 w-4 ${selectedChannels.email ? 'text-blue-600' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                      <span className={`text-sm font-medium ${selectedChannels.email ? 'text-blue-700' : 'text-gray-700'}`}>Email</span>
                    </div>
                  </div>
                  <svg className={`h-4 w-4 text-gray-400 transition-transform ${expandedChannel === 'email' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                </div>
                {expandedChannel === 'email' && (
                  <div className="p-4 bg-white border-t border-gray-100">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Message Body (Optional)</label>
                    <textarea
                      value={customEmailMessage}
                      onChange={(e) => setCustomEmailMessage(e.target.value)}
                      placeholder="Enter custom message to include in the email (e.g. 'We missed you at the last interview stage...')"
                      rows={10}
                      className="w-full text-sm rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 placeholder-gray-400 min-h-[220px]"
                    />
                    <p className="mt-1.5 text-[11px] text-gray-500">This text will be injected into our branded HTML email template above the application link.</p>
                  </div>
                )}
              </div>

              {/* WHATSAPP CHANNEL */}
              <div className={`border-2 rounded-xl overflow-hidden transition-colors ${selectedChannels.whatsapp ? 'border-green-500' : 'border-gray-200'}`}>
                <div 
                  className={`flex items-center justify-between p-3 cursor-pointer ${selectedChannels.whatsapp ? 'bg-green-50/50' : 'bg-white hover:bg-gray-50'}`}
                  onClick={() => setExpandedChannel(expandedChannel === 'whatsapp' ? null : 'whatsapp')}
                >
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={(e) => toggleChannel('whatsapp', e)} className={`flex-shrink-0 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${selectedChannels.whatsapp ? 'bg-green-500 border-green-500' : 'bg-white border-gray-300'}`}>
                      {selectedChannels.whatsapp && <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>}
                    </button>
                    <div className="flex items-center gap-2">
                      <svg className={`h-4 w-4 ${selectedChannels.whatsapp ? 'text-green-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
                      <span className={`text-sm font-medium ${selectedChannels.whatsapp ? 'text-green-700' : 'text-gray-700'}`}>WhatsApp</span>
                    </div>
                  </div>
                  <svg className={`h-4 w-4 text-gray-400 transition-transform ${expandedChannel === 'whatsapp' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                </div>
                {expandedChannel === 'whatsapp' && (
                  <div className="p-4 bg-white border-t border-gray-100 bg-[#EFEAE2] flex justify-center py-5">
                    {/* Fake WhatsApp Message Bubble */}
                    <div className="max-w-[85%] bg-white rounded-lg p-3 shadow-sm relative text-[13px] leading-relaxed text-gray-800">
                      <div className="absolute top-0 -left-2 w-0 h-0 border-t-[10px] border-t-white border-l-[10px] border-l-transparent drop-shadow-sm"></div>
                      <p>Hi {resendTargetName === 'the selected candidates' ? 'Candidate' : resendTargetName.split(' ')[0] || 'Candidate'},</p>
                      <br/>
                      <p>We hope you are having a great day!</p>
                      <br/>
                      <p>We noticed that the following steps are still pending for your application to the <strong>{jobTitle}</strong> role at <strong>{companyName}</strong>.</p>
                      <br/>
                      <p>Please use the secure link below to resume and complete your application:</p>
                      <p className="text-blue-500 underline">https://talentcrux.ai/apply/...</p>
                      <br/>
                      <p>If you have any questions, please contact us at support@talentcrux.ai.</p>
                      <br/>
                      <p>Best regards,<br/>TalentCrux Team</p>
                    </div>
                  </div>
                )}
              </div>

              {/* SMS CHANNEL - Currently disabled pending Twilio phone number configuration
              <div className={`border-2 rounded-xl overflow-hidden transition-colors ${selectedChannels.sms ? 'border-purple-500' : 'border-gray-200'}`}>
                <div 
                  className={`flex items-center justify-between p-3 cursor-pointer ${selectedChannels.sms ? 'bg-purple-50/50' : 'bg-white hover:bg-gray-50'}`}
                  onClick={() => setExpandedChannel(expandedChannel === 'sms' ? null : 'sms')}
                >
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={(e) => toggleChannel('sms', e)} className={`flex-shrink-0 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${selectedChannels.sms ? 'bg-purple-500 border-purple-500' : 'bg-white border-gray-300'}`}>
                      {selectedChannels.sms && <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>}
                    </button>
                    <div className="flex items-center gap-2">
                      <svg className={`h-4 w-4 ${selectedChannels.sms ? 'text-purple-600' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>
                      <span className={`text-sm font-medium ${selectedChannels.sms ? 'text-purple-700' : 'text-gray-700'}`}>Text SMS</span>
                    </div>
                  </div>
                  <svg className={`h-4 w-4 text-gray-400 transition-transform ${expandedChannel === 'sms' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                </div>
                {expandedChannel === 'sms' && (
                  <div className="p-4 bg-white border-t border-gray-100">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Message Body (Optional)</label>
                    <textarea
                      value={customSmsMessage}
                      onChange={(e) => setCustomSmsMessage(e.target.value)}
                      placeholder="Enter custom SMS text. A link will be appended automatically."
                      className="w-full text-sm rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 placeholder-gray-400 min-h-[80px]"
                    />
                    <div className="flex justify-between items-center mt-1.5">
                      <p className="text-[11px] text-gray-500">Keep it short. Standard SMS limits apply.</p>
                      <p className={`text-[11px] font-medium ${customSmsMessage.length > 160 ? 'text-red-500' : 'text-gray-400'}`}>{customSmsMessage.length} / 160</p>
                    </div>
                  </div>
                )}
              </div>
              */}

            </div>
          </div>
        </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={resendingInvite}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={resendingInvite || resendSections.length === 0 || !Object.values(selectedChannels).some(v => v)}
            className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-all shadow-sm"
          >
            {resendingInvite ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Sending…
              </>
            ) : (
              'Send Invites'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
